<script>
import MixedGridFilterAccordion from './MixedGridFilterAccordion';
import MixedGridFiltersItemList from './MixedGridFiltersItemList';
import MixedGridFiltersItemRange from './MixedGridFiltersItemRange';
import MixedGridFiltersSelected from './MixedGridFiltersSelected';
import MixedGridFilters from '@phoenix/mixins/components/mixedGrid/MixedGridFilters';
import Panel from '../common/Panel';
import { closePanel, showPanel } from '@phoenix/helpers/panel-helper';
import { PhxIcon } from '@phx-styleguides/pia';
import { omit } from 'lodash-es';
import { stickyBar } from '@sg/src/View/Components/Organisms/StickyBar';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';

export default {
  components: {
    MixedGridFilterAccordion,
    MixedGridFiltersSelected,
    MixedGridFiltersItemList,
    MixedGridFiltersItemRange,
    Panel,
    PhxIcon,
    PhxsgButton,
  },

  mixins: [MixedGridFilters],

  props: {
    isSticky: {
      type: Boolean,
      default: true,
    },
    large: {
      type: Boolean,
      default: true,
    },
    tight: {
      type: Boolean,
      default: false,
    },
    panelId: {
      type: String,
      default: 'products-filters-panel',
    },
  },

  emits: ['onSortChange', 'onFiltersChanged', 'filtersInitialized'],

  expose: ['removeAllFilters'],

  computed: {
    availableOnline: {
      get() {
        return Object.hasOwn(this.activeFilters, this.sellableFilterId);
      },
      set(value) {
        this.setCheckedFilter(value, this.sellableFilterId, [
          this.filters.find((filter) => filter.id === this.sellableFilterId)?.items[0]?.value || true,
        ]);
      },
    },
    hasQuickFilters() {
      return this.quickFilters?.length > 0;
    },
    quickFilters() {
      return this.filters.filter((filter) => filter.id === this.sellableFilterId);
    },
    mainFilters() {
      return this.filters.filter((filter) => filter.id !== this.sellableFilterId);
    },

    containerClasses() {
      return ['container', { 'container--lg': this.large, 'container--tight': this.tight }];
    },
    shouldDisplayRemoveAllFilters() {
      return (
        Object.values(omit(this.activeFilters, this.unclearableFilters)).reduce(
          (carry, value) => carry + (Array.isArray(value) ? value.length : 1),
          0,
        ) >= 2
      );
    },

    activeFiltersCount() {
      return Object.values(this.activeFilters).reduce((count, values) => count + values.length, 0);
    },

    filtersButtonLabel() {
      if (this.hasFilters) {
        return this.$t('Product.filters.withCount', {
          label: this.$t('Product.filters'),
          count: this.activeFiltersCount,
        });
      }

      return this.$t('Product.filters');
    },
  },

  mounted() {
    const stickyBarEl = document.querySelector('.sticky-bar');
    if (stickyBarEl) {
      stickyBar(stickyBarEl);
    }
  },

  methods: {
    showPanel,

    closePanel,
  },
};
</script>

<template>
  <div
    class="filters-bar filters"
    :class="{ 'sticky-bar': isSticky }"
    :aria-label="$t('Accessibility.ProductFiltersAndSort')"
    data-cy="mixed-grid-filters"
  >
    <div :class="containerClasses">
      <div class="filters-bar__start">
        <button
          class="btn"
          type="button"
          data-cy="open-filters-panel"
          aria-expanded="false"
          @click="showPanel(panelId)"
        >
          {{ filtersButtonLabel }}
          <span class="btn__icon btn__icon--end">
            <phx-icon icon="filters" class="icon--sm" />
          </span>
        </button>
      </div>

      <div class="filters-bar__end" data-cy="mixed-grid-products-count">
        {{ $t('MixedGrid.filters.resultsCount', { count: productsCount }) }}
      </div>
    </div>
  </div>

  <teleport to="#teleport-target-panels">
    <panel
      :id="panelId"
      :title="$t('Product.filters')"
      body-data-cy="mixed-grid-desktop-filters"
      :classes="['products-filters-panel']"
      tight
    >
      <template #content>
        <div class="box">
          <div v-if="hasFilters" class="products-filters-panel__applied-filters-header">
            <span class="products-filters-panel__title h6"> {{ $t('Panel.filters.applied') }}</span>

            <PhxsgButton v-show="shouldDisplayRemoveAllFilters" variant="link" @click="removeAllFilters">
              {{ $t('Search.ResetAll') }}
            </PhxsgButton>
          </div>
          <div v-if="hasFilters" class="products-active-filters">
            <mixed-grid-filters-selected
              hide-reset
              :active-filters="activeFilters"
              :filters="filters"
              :sellable-filter-id="sellableFilterId"
              @remove-filter="removeFilter"
              @remove-all-filters="removeAllFilters"
            />
          </div>
          <div v-if="hasQuickFilters">
            <span class="products-filters-panel__title h6">
              {{ $t('Products.filters.quickFilters') }}
            </span>
            <ul class="products-filters-panel__list">
              <template v-for="filter in quickFilters" :key="`filter-${filter.id}`">
                <mixed-grid-filters-item-list
                  v-if="filter.component === 'list'"
                  :filter="filter"
                  :active-filters="activeFilters"
                  :empty-filters="emptyFilters"
                  @on-item-selected="toggleFilter"
                />
                <mixed-grid-filters-item-range
                  v-if="filter.component === 'range'"
                  :filter="filter"
                  :symbol="getSymbolFromFilter(filter)"
                  :active-filters="activeFilters"
                  @on-range-change="onRangeChange"
                />
              </template>
            </ul>
          </div>
          <ul class="accordion products-filters-panel__accordion">
            <template v-for="(filter, index) in mainFilters" :key="`filter-${filter.id}`">
              <mixed-grid-filter-accordion :id="filter.id" :is-expanded="index === 0" :title="filter.label">
                <mixed-grid-filters-item-list
                  v-if="filter.component === 'list'"
                  :filter="filter"
                  :active-filters="activeFilters"
                  :empty-filters="emptyFilters"
                  @on-item-selected="toggleFilter"
                />
                <mixed-grid-filters-item-range
                  v-else-if="filter.component === 'range'"
                  :filter="filter"
                  :symbol="getSymbolFromFilter(filter)"
                  :active-filters="activeFilters"
                  @on-range-change="onRangeChange"
                />
              </mixed-grid-filter-accordion>
            </template>
          </ul>
        </div>
      </template>

      <template #footer>
        <ul class="actions actions--block" role="presentation">
          <li v-if="hasFilters" role="presentation">
            <PhxsgButton
              variant="plain"
              color="secondary"
              full
              class="actions--panel"
              data-cy="mixed-grid-filters-panel-reset-btn"
              @click="removeAllFilters"
            >
              {{ $t('Search.ResetAll') }}
            </PhxsgButton>
          </li>
          <li role="presentation">
            <PhxsgButton
              variant="plain"
              color="primary"
              full
              class="actions--block"
              :data-tracking="datalayerTrackingViewResults"
              data-cy="mixed-grid-filters-panel-submit-btn"
              @click="closePanel(panelId)"
            >
              {{
                $t('MixedGrid.filters.displayResults', {
                  count: productsCount,
                })
              }}
            </PhxsgButton>
          </li>
        </ul>
      </template>
    </panel>
  </teleport>
</template>
